import React from 'react';
import { Avatar, AvatarProps, AvatarSize, NoSsr } from '@99designs/emu';
import { avatars } from '@99designs/emu/settings';
import { useAppConfig } from '@99designs/layout';
import { linkTo } from '@99designs/routing';
import { AvatarSkeleton } from '../AvatarSkeleton';

export interface UserAvatarProps {
    alt?: string;
    border?: boolean;
    online?: boolean;
    size?: AvatarSize;
    userId: string | number;
    isLinked?: boolean;
}

export const useAvatarUrl = (
    avatarBasePath: string,
    userId?: string | number,
    size: AvatarSize = avatars.size.Medium
) => {
    if (!userId) {
        return '';
    }

    const pixelSize = avatars.sizes[size].avatarSize;
    return `${avatarBasePath}/${userId}/${pixelSize}`;
};

/**
 * This is a wrapper around the UI library's Avatar component, extending it to be explicit about the asset source.
 * `avatarBasePath` refers to the CDN base path in prod, or tapestry in dev.
 */
export function UserAvatar({
    alt,
    border,
    online,
    size = avatars.size.Medium,
    userId,
    isLinked = false,
}: UserAvatarProps) {
    const { avatarBasePath } = useAppConfig();
    const url = useAvatarUrl(avatarBasePath, userId, size);

    // Spread operator is not used here to avoid passing undefined props to the Emu Avatar.

    const uiAvatarProps: AvatarProps = {
        alt,
        border,
        online,
        size,
        url,
    };

    const userAvatar = (
        <NoSsr fallback={<AvatarSkeleton />}>
            <Avatar {...uiAvatarProps} />
        </NoSsr>
    );

    if (isLinked) {
        return (
            <a href={linkTo.userProfile(userId.toString())} data-testid="user-avatar-with-link">
                {userAvatar}
            </a>
        );
    }

    return userAvatar;
}
